import type { ReactNode } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { TableSkeleton } from './Skeleton';

export interface Column<T> {
  key: string;
  header: string;
  /** Renders the cell. */
  render: (row: T) => ReactNode;
  /** Sort key passed to onSortChange when this column header is clicked. Omit to disable sort. */
  sortKey?: string;
  width?: string;
  align?: 'left' | 'right' | 'center';
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[] | null;
  rowKey: (row: T) => string;
  loading?: boolean;
  emptyState?: ReactNode;
  onRowClick?: (row: T) => void;
  /** Active sort: a string token matching one of the columns' sortKey, suffixed with `_asc` or `_desc`. */
  sort?: string;
  onSortChange?: (next: string) => void;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  emptyState,
  onRowClick,
  sort,
  onSortChange,
}: Props<T>) {
  const sortKey = sort?.replace(/_(asc|desc)$/, '') ?? '';
  const sortDir = sort?.endsWith('_asc') ? 'asc' : sort?.endsWith('_desc') ? 'desc' : null;

  function handleSortClick(col: Column<T>) {
    if (!col.sortKey || !onSortChange) return;
    if (sortKey !== col.sortKey) {
      onSortChange(`${col.sortKey}_desc`);
      return;
    }
    onSortChange(sortDir === 'desc' ? `${col.sortKey}_asc` : `${col.sortKey}_desc`);
  }

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            {columns.map((c) => {
              const active = sortKey && sortKey === c.sortKey;
              return (
                <th
                  key={c.key}
                  scope="col"
                  style={c.width ? { width: c.width } : undefined}
                  className={`px-4 py-2.5 text-${c.align ?? 'left'} text-[10px] font-black uppercase tracking-widest text-brand-muted ${
                    c.sortKey ? 'cursor-pointer select-none hover:text-brand-primary' : ''
                  }`}
                  onClick={() => handleSortClick(c)}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.header}
                    {c.sortKey && active && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading || rows === null ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-6">
                <TableSkeleton rows={6} cols={columns.length} />
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>{emptyState}</td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? 'hover:bg-gray-50 cursor-pointer transition-colors' : ''}
              >
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 text-${c.align ?? 'left'} text-brand-primary`}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}
